import { Component, OnInit, OnDestroy, Output, EventEmitter, Input, ViewChild, AfterViewInit } from "@angular/core";
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from "@angular/forms";
import { firstValueFrom, Subject, takeUntil } from "rxjs";
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";
import { TranslatePipe } from "@ngx-translate/core";
import { CommonModule, DatePipe } from "@angular/common";
import { MatFormFieldModule, MatLabel } from "@angular/material/form-field";
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS } from "@angular/material/core";
import { MultilingualDateAdapter } from "../../../../adapters/multilingual-date.adapter";
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { EstimateSearchModel } from "../../../../models/EstimateSearchResponse";
import { unicodeValidator } from "../../../../custom-validator/custom-validator";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Business } from "../../../../models/BusinessDropdownResponse";
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { EstimateSearchRequest } from "../../../../models/EstimateSearchRequest";
import { CONSTANTS } from "../../../../Constants/Constants";
import { OpenEstimateApiService } from "../../../../services/api/open-estimate.service";
import { EstimateSearchDropDownDataResponse } from "../../../../models/EstimateSearchDropDownDataResponse";
import { CommonDataService } from '../../../../services/common-data.service';
import { DateControlType } from "../../../../models/enum";
import { CollaborationState, DropDownItem, OpenEstimateRequest, SegmentDetails, SubSegmentDetails } from "../../../../models/dtos";
import { IOpenEstimateFormGroup } from "../../../../models/IOpenEstimateFormGroup";
import { CommonFunctionsService } from "../../../../services/common-functions.service";
import { EstimateCloseService } from "../../../../services/estimate-close.service";
import { EstimateSaveService } from "../../../../services/estimate-save.service";
import { UserProfileService } from "../../../../services/common/user-profile.service";
import { DecimalPrecisionPipe } from "../../../../pipes/decimal-precision-pipe/decimal-precision.pipe";
import { MultilingualDatePipe } from "../../../../pipes/multilingual-date-pipe/multilingual-date-pipe";
import { OpenEstimateOperationsService } from "../../../../services/common/open-estimate-operations.service";
import { CostReviewEstimateType, ReviewDetailTabsService, IOptionalWorkflowAndEstimateType } from "review-detail-tabs";
import { GetReviewDetailsService } from "../../../../services/api/get-review-details.service";
import { ApiPollingService } from "../../../../services/common/api-polling.service";
import { HttpErrorResponse } from "@angular/common/http";
import { MatSort, MatSortModule } from '@angular/material/sort';
import { CommonMetaDataService } from "../../../../services/common-meta-data.service";
import { IOpenEstimateDropdownCollections } from "../../../../models/IOpenEstimateDropdownCollections";
import { SaveConfirmationDialogComponent } from "../save-confirmation-dialog/save-confirmation-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { CloseOnEscapeDirective } from "../../../../directives/close-on-escape.directive";
import { TranslateService } from "@ngx-translate/core";
import { MultilingualCurrencyPipe } from "../../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe";
import { GocCollaborationRestrictionHandlerService } from "../../../../services/common/goc-collaboration-restriction-handler.service";
import { NotificationMessageService } from "../../../../services/common/notification-message.service";

type EstimateRow = EstimateSearchModel & {
  modifiedDateEpoch: number;
  creationDateEpoch: number;
  startDateEpoch: number;
  endDateEpoch: number;
};

@Component({
  selector: 'open-estimate',
  standalone: true,
  templateUrl: 'open-estimate.component.html',
  styleUrls: ['open-estimate.component.scss'],
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatLabel,
    MatDatepickerModule,
    MatSelectModule,
    MatTableModule,
    MatPaginatorModule,
    MatTooltipModule,
    TranslatePipe,
    MatCheckboxModule,
    DecimalPrecisionPipe,
    MultilingualDatePipe,
    MatSortModule,
    CloseOnEscapeDirective,
    MultilingualCurrencyPipe
  ],
  providers: [{ provide: DateAdapter, useClass: MultilingualDateAdapter }, { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS }, DatePipe]
})
export class OpenEstimateComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() cancel = new EventEmitter<void>();
  @Output() navigateToEstimateTab = new EventEmitter<void>();
  private destroy$ = new Subject<void>();

  @Input() fromCreateOpportunity: boolean = false;
  @Output() selectedEstimateId = new EventEmitter<string>();
  @ViewChild(MatSort) sort: MatSort;

  businessList: Business[] = [];
  segmentList: SegmentDetails[] = [];
  hierarchyList: SubSegmentDetails[] = [];
  typeList: DropDownItem[] = [];
  statusList: DropDownItem[] = [];
  dateRangeList: { key: number, label: string }[] = [{ 'key': 1, 'label': 'Date Modified' }, { 'key': 2, 'label': 'Date Created' }];
  datesList: { key: number, label: string }[] = [{ 'key': 0, 'label': 'All Dates' }, { 'key': 1, 'label': 'Before' }, { 'key': 2, 'label': 'After' }, { 'key': 3, 'label': 'During' }];
  displayedColumnsEstimateSearch: string[] = ['estimateID', 'estimateName', 'estimateDescription', 'dateModified', 'dateCreated', 'estimateBusiness', 'estimateSegment', 'estimateType', 'estimateStatus', 'customerName', 'estimateStartDate', 'estimateEndDate', 'estimateSource', 'sellPrice', 'gm', 'collaborative', 'currentEditor', 'costApproved'];
  openEstimateDropdownresponse: EstimateSearchDropDownDataResponse = new EstimateSearchDropDownDataResponse();
  openEstimateDropdownCollections: IOpenEstimateDropdownCollections = {
    business: [],
    segment: [],
    subSegment: [],
    estimateType: [],
    status: []
  };
  dataSource: EstimateRow[] = [];
  totalCount: number = 0;
  currentPageNumber: number = 1;
  pageSize: number = CONSTANTS.HUNDRED;
  gotoPageNumber: number | null = null;
  months: { key: number, label: string }[] = [{ 'key': 1, 'label': 'January' }, { 'key': 2, 'label': 'February' }, { 'key': 3, 'label': 'March' }, { 'key': 4, 'label': 'April' }, { 'key': 5, 'label': 'May' }, { 'key': 6, 'label': 'June' }, { 'key': 7, 'label': 'July' }, { 'key': 8, 'label': 'August' }, { 'key': 9, 'label': 'September' }, { 'key': 10, 'label': 'October' }, { 'key': 11, 'label': 'November' }, { 'key': 12, 'label': 'December' }];
  years: number[] = [];
  minDate: Date = new Date();
  maxDate: Date = new Date();
  openEstimateFormgroup!: FormGroup<IOpenEstimateFormGroup>;
  cultureId: number = CONSTANTS.ONE;
  estimateTableData = new MatTableDataSource<EstimateRow>([]);


  selection = new SelectionModel<EstimateSearchModel>(false, []);
  constructor(
    private openEstimateApiService: OpenEstimateApiService,
    private datePipe: DatePipe,
    private readonly userProfileService: UserProfileService,
    private readonly commonDataService: CommonDataService,
    private readonly fb: NonNullableFormBuilder,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly estimateCloseOperationsService: EstimateCloseService,
    private readonly estimateSaveService: EstimateSaveService,
    private readonly openEstimateOperationsService: OpenEstimateOperationsService,
    private readonly reviewDetailsTabService: ReviewDetailTabsService,
    private readonly apiPollingService: ApiPollingService,
    private readonly commonMetaDataService: CommonMetaDataService,
    private readonly dialog: MatDialog,
    private readonly notificationMessageService: NotificationMessageService,
    public readonly translate: TranslateService,
    private readonly gocCollaborationRestrictionHandlerService: GocCollaborationRestrictionHandlerService) {
    this.initializeForm();
  }

  ngOnInit() {
    const cultureString = this.commonDataService.getUserLanguagePreference();
    this.cultureId = cultureString ? this.commonFunctionsService.getCultureId(cultureString) : CONSTANTS.ONE;
    this.getDropdownData();
    this.initializeYears();
  }
  ngAfterViewInit() {
    this.estimateTableData.sort = this.sort;
    this.estimateTableData.sortingDataAccessor = (item, property) => {
      let value: string | number;
      switch (property) {
        case 'estimateDescription': value = item.description; break;
        case 'dateModified': return item.modifiedDateEpoch;
        case 'dateCreated': return item.creationDateEpoch;
        case 'estimateBusiness': value = item.business; break;
        case 'estimateSegment': value = item.segment; break;
        case 'estimateStartDate': return item.startDateEpoch;
        case 'estimateEndDate': return item.endDateEpoch;
        case 'customerName': value = item.customer; break;
        case 'estimateSource': value = item.source; break;
        default: value = (item as any)[property];
      }

      return typeof value === 'string' ? value.toLocaleLowerCase() : value;
    };
  }

  get estimateId(): FormControl<string> {
    return this.openEstimateFormgroup.controls.estimateID as FormControl<string>;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getDropdownData() {
    const metaData = this.commonMetaDataService.getMetaData();
    this.businessList = metaData.business || [];
    this.segmentList = metaData.segments || [];
    this.hierarchyList = metaData.subSegments || [];
    this.typeList = metaData.estimateType || [];
    this.statusList = metaData.status || [];
    this.openEstimateDropdownCollections = structuredClone({
      business: this.businessList,
      segment: this.segmentList,
      subSegment: this.hierarchyList,
      estimateType: this.typeList,
      status: this.statusList
    });
  }

  initializeForm() {
    this.openEstimateFormgroup = this.fb.group<IOpenEstimateFormGroup>({
      estimateID: this.fb.control<string>(CONSTANTS.EMPTY_STRING, [unicodeValidator()]),
      estimateName: this.fb.control<string>(CONSTANTS.EMPTY, [unicodeValidator()]),
      estimateDescription: this.fb.control<string>(CONSTANTS.EMPTY, [unicodeValidator()]),
      estimateBusiness: this.fb.control<string>({ value: CONSTANTS.EMPTY, disabled: false }),
      estimateSegment: this.fb.control<number>({ value: CONSTANTS.NEGATIVE_ONE, disabled: false }),
      estimateHierarchy: this.fb.control<number>({ value: CONSTANTS.NEGATIVE_ONE, disabled: false }),
      estimateType: this.fb.control<number>({ value: CONSTANTS.ZERO, disabled: false }),
      estimateStatus: this.fb.control<number>({ value: CONSTANTS.ZERO, disabled: false }),
      customerName: this.fb.control<string>(CONSTANTS.EMPTY, [unicodeValidator()]),
      estimateStartDate: this.fb.control<string>(CONSTANTS.EMPTY),
      estimateEndDate: this.fb.control<string>(CONSTANTS.EMPTY),
      dateRange: this.fb.control<number>(CONSTANTS.ONE),
      dates: this.fb.control<number>(CONSTANTS.ZERO),
      month: this.fb.control<number>({ value: CONSTANTS.ZERO, disabled: true }),
      year: this.fb.control<number>({ value: CONSTANTS.ZERO, disabled: true }),
      createdBy: this.fb.control<string>(CONSTANTS.EMPTY, [unicodeValidator()]),
      modifiedBy: this.fb.control<string>(CONSTANTS.EMPTY, [unicodeValidator()]),
      checkArchive: this.fb.control<boolean>(false)
    })
  }



  onDblClick(row: EstimateSearchModel): void {
    this.selection.select(row);
    if (this.fromCreateOpportunity) {
      this.selectEstimate();
    }
    else this.openEstimate();
  }

  async openEstimate(): Promise<void> {
    const canOpen = await this.handleAnyUnsavedChanges();
    if (!canOpen) {
      return;
    }
    let payload: OpenEstimateRequest = {
      userId: this.userProfileService.userInfo?.gid ?? CONSTANTS.EMPTY_STRING,
      estimateId: this.selection.selected[CONSTANTS.ZERO].estimateID,
      userEmailId: this.userProfileService.userInfo?.mail ?? CONSTANTS.EMPTY_STRING,
      culture: this.commonDataService.getCurrentApplicationCulture(),
      isArchive: this.openEstimateFormgroup.controls.checkArchive.value ?? false,
    }

    // Do NOT tie this to destroy$: handleAnyUnsavedChanges() has already closed
    // the current estimate, so the request must complete even if the host
    // component (e.g. inline <open-estimate> in side-nav-bar on the Reports
    // tab) is torn down by the reactive cascade from resetEstimateState().
    this.openEstimateApiService.openEstimate(payload)
      .subscribe({
        next: async (openEstimate) => {
          if (openEstimate.showGOCAccessWarning) {
            this.notificationMessageService.showWarning('GOC_ACCESS_WARNING_MESSAGE', 'RESTRICTED');
          }
          await this.openEstimateOperationsService.openEstimate(openEstimate);
          this.checkIsSyncNeeded();
          this.navigateToEstimateTab.emit();
          // Root-scoped fallback: fires even if this component was destroyed
          // by the resetEstimateState() cascade before the response arrived.
          this.commonDataService.requestNavigateToEstimateTab();
        },
        error: async (error: HttpErrorResponse) => {
          if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
            return;
          }
        }
      });
  }
  private checkIsSyncNeeded(): void {
    this.apiPollingService.isSyncNeeded().subscribe({
      next: (isSyncNeeded) => {
        this.commonDataService.setIsSyncNeeded(isSyncNeeded);
      },
      error: () => {
        this.commonDataService.setIsSyncNeeded(true);
      }
    });
  }

  //TO-DO: visibility of review Tab is not handled as of now, need to handle that once estimate tab data is binded.
  setEstimateTypeBasedOnSellPrice(userCostReviewThresholdValue: number, isCostReviewUnderThreshold: boolean) {
    const estimateTab = this.commonDataService.getEstimateMetaData()?.estimateTab;

    if (estimateTab && estimateTab.soldASSellPrice) {
      let data: IOptionalWorkflowAndEstimateType;
      const sellPrice = estimateTab.soldASSellPrice;
      if (sellPrice < userCostReviewThresholdValue && isCostReviewUnderThreshold) {
        data = {
          optionalWorkflow: true,
          optionalEstimateType: CostReviewEstimateType.EstimateLess50K
        }
      } else if (sellPrice <= CONSTANTS.MAX_ESTIMATE_VALUE) {
        data = {
          optionalWorkflow: false,
          optionalEstimateType: CostReviewEstimateType.Estimate50_150K
        }
      }
      else {
        data = {
          optionalWorkflow: false,
          optionalEstimateType: CostReviewEstimateType.EstimateGreater150K
        }
      }
      this.reviewDetailsTabService.setOptionalWorkflowAndEstimateType(data);
    }
  }

  searchEstimate() {
    if (!this.openEstimateFormgroup.valid) {
      return;
    }

    this.currentPageNumber = CONSTANTS.ONE;
    this.fetchEstimates();
  }

  get totalPages(): number {
    return this.pageSize > CONSTANTS.ZERO ? Math.ceil(this.totalCount / this.pageSize) : CONSTANTS.ZERO;
  }

  onPageChange(event: PageEvent) {
    this.currentPageNumber = event.pageIndex + CONSTANTS.ONE;
    this.fetchEstimates();
  }

  gotoPage() {
    if (this.gotoPageNumber && this.gotoPageNumber >= CONSTANTS.ONE && this.gotoPageNumber <= this.totalPages) {
      this.currentPageNumber = this.gotoPageNumber;
      this.gotoPageNumber = null;
      this.fetchEstimates();
    }
  }

  private fetchEstimates() {
    const searchEstimateRequest: EstimateSearchRequest = this.createSearchEstimateRequest();
    this.openEstimateApiService.searchEstimateService(searchEstimateRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.dataSource = (response.result ?? []).map((item) => ({
          ...item,
          modifiedDateEpoch: this.toTimestamp(item.modifiedDate),
          creationDateEpoch: this.toTimestamp(item.creationDate),
          startDateEpoch: this.toTimestamp(item.startDate),
          endDateEpoch: this.toTimestamp(item.endDate)
        }));
        this.totalCount = response.totalCount;
        this.estimateTableData.data = this.dataSource;
      });
  }


  createSearchEstimateRequest(): EstimateSearchRequest {
    this.calculateMinMaxDate();
    this.commonDataService.setUserId(this.userProfileService.userInfo?.gid ?? CONSTANTS.EMPTY_STRING);
    let request = {
      owner: this.userProfileService.userInfo?.gid ?? CONSTANTS.EMPTY_STRING,
      estimateID: this.openEstimateFormgroup.controls.estimateID.value,
      estimateName: this.openEstimateFormgroup.controls.estimateName.value,
      estimateDescription: this.openEstimateFormgroup.controls.estimateDescription.value,
      estimateTypeId: this.openEstimateFormgroup.controls.estimateType.value,
      estimateStatusId: this.openEstimateFormgroup.controls.estimateStatus.value,
      estimateBusiness: this.openEstimateFormgroup.controls.estimateBusiness.value,
      estimateSegment: this.openEstimateFormgroup.controls.estimateSegment.value,
      estimateSubSegment: this.openEstimateFormgroup.controls.estimateHierarchy.value,
      dateCriteria: this.openEstimateFormgroup.controls.dateRange.value,
      minDate: this.datePipe.transform(this.minDate, CONSTANTS.DATE_FORMAT),
      maxDate: this.datePipe.transform(this.maxDate, CONSTANTS.DATE_FORMAT),
      estimateStartDate: this.changeDateFormat(this.openEstimateFormgroup.controls.estimateStartDate.value),
      estimateEndDate: this.changeDateFormat(this.openEstimateFormgroup.controls.estimateEndDate.value),
      customerName: this.openEstimateFormgroup.controls.customerName.value,
      cultureID: this.cultureId,
      createdBy: this.openEstimateFormgroup.controls.createdBy.value,
      modifiedBy: this.openEstimateFormgroup.controls.modifiedBy.value,
      checkArchive: this.openEstimateFormgroup.controls.checkArchive.value,
      pageNumber: this.currentPageNumber
    }
    return request;
  }

  changeDateFormat(date: string | undefined): string {
    const d = date ? new Date(date) : CONSTANTS.OPEN_ESTIMATE_START_END_DEFAULT_DATE;
    return this.datePipe.transform(d, CONSTANTS.OPEN_ESTIMATE_DATE_FORMAT) || CONSTANTS.EMPTY;
  }

  onBusinessChange(event: string) {
    this.openEstimateFormgroup.controls.estimateSegment.setValue(CONSTANTS.NEGATIVE_ONE);
    this.openEstimateFormgroup.controls.estimateHierarchy.setValue(CONSTANTS.NEGATIVE_ONE);
    this.filterSegmentList();
    this.filterHierarchyList();
  }

  onSegmentChange(event: string) {
    this.openEstimateFormgroup.controls.estimateHierarchy.setValue(CONSTANTS.NEGATIVE_ONE);
    this.filterHierarchyList();
  }

  filterSegmentList() {
    const selectedBusiness = this.openEstimateFormgroup.controls.estimateBusiness.value;
    if (selectedBusiness.length === 0) {
      this.segmentList = this.openEstimateDropdownCollections.segment;
      return;
    }
    this.segmentList = this.openEstimateDropdownCollections.segment.filter((segment: SegmentDetails) => segment.business === selectedBusiness);
  }

  filterHierarchyList() {
    const selectedBusiness = this.openEstimateFormgroup.controls.estimateBusiness.value;
    const selectedSegment = this.openEstimateFormgroup.controls.estimateSegment.value;
    this.hierarchyList = this.openEstimateDropdownCollections.subSegment.filter((hierarchy: SubSegmentDetails) => this.filterHierarchyValue(hierarchy, selectedBusiness, selectedSegment));
  }

  filterHierarchyValue(hierarchy: SubSegmentDetails, selectedBusiness: string, selectedSegment: number): boolean {
    if (selectedSegment !== CONSTANTS.NEGATIVE_ONE && selectedBusiness) {
      return hierarchy.segmentId === selectedSegment && hierarchy.business === selectedBusiness;
    } else if (selectedSegment === CONSTANTS.NEGATIVE_ONE && selectedBusiness) {
      return hierarchy.business === selectedBusiness;
    } else if (selectedSegment !== CONSTANTS.NEGATIVE_ONE && !selectedBusiness) {
      return hierarchy.segmentId === selectedSegment;
    } else {
      return true;
    }
  }
  resetForm() {
    this.openEstimateFormgroup.reset();
    this.openEstimateFormgroup.controls.dateRange.setValue(CONSTANTS.ONE);
    this.openEstimateFormgroup.controls.dates.setValue(CONSTANTS.ZERO);
    this.openEstimateFormgroup.controls.month.disable();
    this.openEstimateFormgroup.controls.year.disable();
  }
  initializeYears() {
    const startYear = CONSTANTS.TWO_THOUSAND_SEVEN;
    const currentYear = new Date().getFullYear();
    this.years = [];
    for (let year = startYear; year <= currentYear; year++) {
      this.years.push(year);
    }
    this.years.reverse();
  }

  onDatesChange(value: number) {
    const yearControl = this.openEstimateFormgroup.controls.year;
    const monthControl = this.openEstimateFormgroup.controls.month;
    if (value === CONSTANTS.ZERO) {
      monthControl.disable();
      yearControl.disable();
      yearControl.setValue(CONSTANTS.ZERO);
      monthControl.setValue(CONSTANTS.ZERO);
    } else {
      const selectedMonth = monthControl.value ? monthControl.value : this.months[0].key;
      const selectedYear = yearControl.value ? yearControl.value : this.years[0];
      monthControl.enable();
      yearControl.enable();
      monthControl.setValue(selectedMonth);
      yearControl.setValue(selectedYear);
    }
  }

  calculateMinMaxDate() {
    const dateControl = this.openEstimateFormgroup.controls.dates.value;
    const month = this.openEstimateFormgroup.controls.month.value;
    const year = this.openEstimateFormgroup.controls.year.value;

    switch (dateControl) {
      case DateControlType.ALL_DATES: {
        this.minDate = new Date(CONSTANTS.TWO_THOUSAND_SEVEN, CONSTANTS.ZERO, CONSTANTS.ONE);
        this.maxDate = new Date(new Date().getFullYear(), CONSTANTS.ELEVEN, CONSTANTS.THIRTY_ONE);
        break;
      }
      case DateControlType.BEFORE: {
        const selectedMonth = Number(month) - CONSTANTS.ONE;
        this.minDate = new Date(CONSTANTS.TWO_THOUSAND_SEVEN, CONSTANTS.ZERO, CONSTANTS.ONE);
        this.maxDate = new Date(Number(year), selectedMonth, CONSTANTS.ONE);
        break;
      }
      case DateControlType.AFTER: {
        if (Number(month) === CONSTANTS.TWELVE) {
          const nextMonth = CONSTANTS.ZERO;
          const nextYear = Number(year) + CONSTANTS.ONE;
          this.minDate = new Date(nextYear, nextMonth, CONSTANTS.ONE);
          this.maxDate = new Date(new Date().getFullYear(), CONSTANTS.ELEVEN, CONSTANTS.THIRTY_ONE);
        } else {
          this.minDate = new Date(Number(year), Number(month), CONSTANTS.ONE);
          this.maxDate = new Date(new Date().getFullYear(), CONSTANTS.ELEVEN, CONSTANTS.THIRTY_ONE);
        }
        break;
      }
      case DateControlType.DURING: {
        if (Number(month) === CONSTANTS.TWELVE) {
          const selectedMonth = Number(month) - CONSTANTS.ONE;
          const nextMonth = CONSTANTS.ZERO;
          const nextYear = Number(year) + CONSTANTS.ONE;
          this.minDate = new Date(Number(year), selectedMonth, CONSTANTS.ONE);
          this.maxDate = new Date(nextYear, nextMonth, CONSTANTS.ONE);
        } else {
          const selectedMonth = Number(month) - CONSTANTS.ONE;
          this.minDate = new Date(Number(year), selectedMonth, CONSTANTS.ONE);
          this.maxDate = new Date(Number(year), Number(month), CONSTANTS.ONE);
        }
        break;
      }
      default: {
        break;
      }
    }
  }
  dismissClick(): void {
    this.cancel.emit();
  }


  selectEstimate(): void {
    const selectedEstimate = this.selection.selected[CONSTANTS.ZERO];
    this.selectedEstimateId.emit(selectedEstimate.estimateID);
  }
  async handleAnyUnsavedChanges(): Promise<boolean> {
    if (this.commonDataService.getCollaborationState() === CollaborationState.Edit) {
      if (!await this.estimateCloseOperationsService.collaborativeChecksBeforeClose(
        () => this.estimateSaveService.createSaveEstimateRequest())) {
        return false;
      }
      await this.estimateCloseOperationsService.closeEstimate();
      return true;
    }
    if (this.commonDataService.isSaveEnabled && !this.commonDataService.isReadOnlyMode()) {
      const confirmationDialogRef = this.dialog.open(SaveConfirmationDialogComponent, {
        disableClose: true,
        data: { estimateName: this.commonDataService.getEstimateName() }
      });
      const okClicked = await firstValueFrom(confirmationDialogRef.afterClosed());
      if (okClicked === null || okClicked === undefined) {
        return false;
      }
      if (okClicked) {
        await this.estimateSaveService.save();
      }
      this.commonDataService.isSaveEnabled = false;
    }
    await this.estimateCloseOperationsService.closeEstimate();
    return true;
  }

  toTimestamp(date: Date): number {
    if (!date) {
      return 0;
    }
    const ts = new Date(date).getTime();
    return Number.isFinite(ts) ? ts : 0;
  }
}
